import {
  fieldsetProps, getCaptchaErrorMessage, getCaptchaSiteKey, merge, newGetFieldLabel,
  newGetFieldPlaceholder, newGetInitialValues, newGetSelectFieldOptions,
  newHasField, newIsFieldRequired, newValidate, Translation as T, useAmsSelector,
  useCaptcha
} from "@awe-web/shared/lib";
import { Formik } from "formik";
import PropTypes from "prop-types";
import { Button, ErrorMessage, RequiredNotice, ScrollToError } from "../../../components/form.js";
import { Col, Row } from "../../../components/grid.js";
import { ScreenSectionHeading } from "../../../components/screen.js";
import {
  AccountFieldset,
  AddressFieldset,
  ConsentFieldset,
  IdentificationAndDriversLicenseFieldset
} from "../../../user/components/form.js";

import "./my_bmw_rent_profile_form.scss";
import { useCallback } from "react";
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useSelector } from "react-redux";

const NOTICE = "FSD0000209";
const SUBMIT = "FSD0000236";
const LEGAL = "FSD0000197";

export function InternalProfileForm(props) {
  const { handleSubmit, onCaptchaChange } = props;

  const captchaPublicSiteKey = useSelector((state) => getCaptchaSiteKey(state));
  const { captchaRef, handleCaptcha, currentLanguage } = useCaptcha();
  const captchaErrorMessage = useSelector((state) => getCaptchaErrorMessage(state));

  const handleCaptchaChange = useCallback((token) => {
    onCaptchaChange(token);
    handleCaptcha(token);
  }, [handleCaptcha, onCaptchaChange]);

  return (
    <form onSubmit={handleSubmit}>
      <ScrollToError />
      <AccountFieldset {...fieldsetProps(props)} />
      <AddressFieldset {...fieldsetProps(props)} />
      <IdentificationAndDriversLicenseFieldset {...fieldsetProps(props)} />

      <Row>
        <Col desktop={{ cols: 8, offset: 2 }}>
          <ScreenSectionHeading light><T code={LEGAL} /></ScreenSectionHeading>
        </Col>
      </Row>
      <ConsentFieldset
        className="my-bmw-rent-profile-form__consent-fieldset"
        {...fieldsetProps(props)}
      />

      {captchaErrorMessage && <Row>
        <Col tablet={{ cols: 4, offset: 4 }} desktop={{ cols: 4, offset: 6 }}>
          <ErrorMessage error={captchaErrorMessage} />
        </Col>

      </Row>
      }

      {captchaPublicSiteKey && <Row className="my-bmw-rent-profile-form__captcha">
        <Col tablet={{ cols: 4, offset: 4 }} desktop={{ cols: 4, offset: 6 }}>
          <HCaptcha
            ref={captchaRef}
            sitekey={captchaPublicSiteKey}
            onVerify={handleCaptchaChange}
            hl={currentLanguage}
          />
        </Col>
      </Row>
      }

      <Row>
        <Col tablet={{ cols: 4, offset: 4 }} desktop={{ cols: 4, offset: 6 }}>
          <Button primary continue submit className="my-bmw-rent-profile-form__submit-button"><T code={SUBMIT} /></Button>
        </Col>
      </Row>

      <Row className="my-bmw-rent-profile-form__required-notice">
        <Col desktop={{ cols: 8, offset: 2 }}>
          <RequiredNotice><T code={NOTICE} /></RequiredNotice>
        </Col>
      </Row>
    </form>
  );
}

InternalProfileForm.valuesShape = merge(
  AccountFieldset.valuesShape,
  AddressFieldset.valuesShape,
  IdentificationAndDriversLicenseFieldset.valuesShape
);
InternalProfileForm.propTypes = {
  values: PropTypes.shape(InternalProfileForm.valuesShape),
  isFieldRequired: PropTypes.func,
  getSelectFieldOptions: PropTypes.func,
  getFieldLabel: PropTypes.func,
  getFieldPlaceholder: PropTypes.func,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  handleSubmit: PropTypes.func
};

export default function MyBmwRentProfileForm({ onSubmit, onCaptchaChange }) {
  const isFieldRequired = useAmsSelector((state) => newIsFieldRequired("privateChangeUserDataFSD")(state));
  const getFieldLabel = useAmsSelector((state) => newGetFieldLabel("privateChangeUserDataFSD")(state));
  const getFieldPlaceholder = useAmsSelector((state) => newGetFieldPlaceholder("privateChangeUserDataFSD")(state));
  const getSelectFieldOptions = useAmsSelector((state) => newGetSelectFieldOptions("privateChangeUserDataFSD")(state));
  const hasField = useAmsSelector((state) => newHasField("privateChangeUserDataFSD")(state));

  const validate = useAmsSelector((state) => newValidate("privateChangeUserDataFSD", {
    "Address:email": "Customer:login",
    "AlternativeWebLogin:Login": "Customer:login"
  })(state));

  const initialValues = useAmsSelector((state) => {
    const formInitializer = newGetInitialValues("privateChangeUserDataFSD");
    const inialValuesFactory = formInitializer(state);
    return inialValuesFactory({
      [AccountFieldset.PREFIX_KEY]: "",
      [AccountFieldset.NAME]: "",
      [AccountFieldset.SURNAME]: "",
      [AccountFieldset.BIRTHDATE]: "",
      [AccountFieldset.PHONE]: "",
      [AccountFieldset.USERNAME]: "",
      [AccountFieldset.PASSWORD]: "",
      ...(hasField(AccountFieldset.TAX_IDENT) ? { [AccountFieldset.TAX_IDENT]: "" } : {}),

      [AddressFieldset.STREET]: "",
      [AddressFieldset.BUILDING]: "",
      [AddressFieldset.ZIP_CODE]: "",
      [AddressFieldset.CITY]: "",
      [AddressFieldset.COUNTRY_CODE]: "",

      [IdentificationAndDriversLicenseFieldset.IDENT_CARD_TYPE]: "",
      [IdentificationAndDriversLicenseFieldset.IDENT_CARD_NO]: "",
      [IdentificationAndDriversLicenseFieldset.IDENT_CARD_COUNTRY]: "",
      [IdentificationAndDriversLicenseFieldset.IDENT_CARD_AUTHORITY]: "",
      [IdentificationAndDriversLicenseFieldset.IDENT_CARD_DATE_ISSUED]: undefined,
      [IdentificationAndDriversLicenseFieldset.IDENT_CARD_VALID_TILL]: undefined,
      [IdentificationAndDriversLicenseFieldset.DRIVERS_LICENSE_NO]: "",
      [IdentificationAndDriversLicenseFieldset.DRIVERS_LICENSE_COUNTRY]: "",
      [IdentificationAndDriversLicenseFieldset.DRIVERS_LICENSE_DATE_ISSUED]: undefined,

      [ConsentFieldset.MAIL]: false,
      [ConsentFieldset.EMAIL]: false,
      [ConsentFieldset.PHONE]: false
    });
  });

  return (
    <Formik initialValues={initialValues} validate={validate} onSubmit={onSubmit}>
      {(formikProps) => (
        <InternalProfileForm
          {...formikProps}
          isFieldRequired={isFieldRequired}
          getSelectFieldOptions={getSelectFieldOptions}
          getFieldLabel={getFieldLabel}
          getFieldPlaceholder={getFieldPlaceholder}
          onCaptchaChange={onCaptchaChange}
        />
      )}
    </Formik>
  );
}

MyBmwRentProfileForm.propTypes = {
  onSubmit: PropTypes.func
};
